import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikErrors } from 'formik';

// project import
import { DateFormat, MAX_DATE } from 'utils/constants';

// material-ui
import { FormControl, FormHelperText, TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

// types
import { GenericCardProps, KeyedObject } from 'models';
import i18n from 'i18n';
import { enUS } from 'date-fns/locale';
import sk from 'date-fns/esm/locale/sk';
import { useAppSelector } from 'hooks/useAppStore';
import { selectAjaxLoader } from 'features/app/appSelectors';

interface FormControlDatePickerProps {
  captionLabel?: string;
  errorMessage?: string;
  helperText?: string;
  iconPrimary?: GenericCardProps['iconPrimary'];
  iconSecondary?: GenericCardProps['iconPrimary'];
  placeholder?: string;
  textPrimary?: string;
  textSecondary?: string;
  onChange: (e: Date | null) => any;
  onBlur?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => any;
  disabled?: boolean;
  type?: string;
  endAdornment?: string;
  name: string;
  value: any;
  isMultiline?: boolean;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<void> | Promise<FormikErrors<KeyedObject>>;
}

// ==============================|| FORM CONTROL ||============================== //

const FormControlDatePicker = ({
  captionLabel,
  errorMessage,
  setFieldValue,
  name,
  value,
  disabled
}: FormControlDatePickerProps) => {
  const { t } = useTranslation();
  const errorState = !!errorMessage?.length;
  const operationInProgress = useAppSelector(selectAjaxLoader);

  return (
    <FormControl fullWidth error={errorState}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={i18n.language === 'en' ? enUS : sk}
      >
        <DatePicker
          renderInput={(props) => (
            <TextField fullWidth {...props} helperText='' />
          )}
          label={t(captionLabel || '')}
          value={value}
          inputFormat={DateFormat.BUSINESS_DAY_FORMAT}
          onChange={(e) => {
            setFieldValue(name, e);
          }}
          disabled={disabled ?? operationInProgress}
          maxDate={MAX_DATE}
        />
      </LocalizationProvider>
      {errorState && (
        <FormHelperText error id={`${name}-error`}>
          {t(errorMessage)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

FormControlDatePicker.defaultProps = {
  disabled: false
};

export default FormControlDatePicker;
